<template>
  <div
    v-tooltip.placementRight="disabledMessage || ''"
    :class="{
      'tw-pb-2': isLeftDrawerOpen,
      'tw-pb-1': !isLeftDrawerOpen,
      'tw-opacity-50 tw-cursor-not-allowed [&_*]:!tw-cursor-not-allowed':
        !!disabledMessage,
    }"
  >
    <template v-if="hasSelectedWorkflow">
      <div
        v-show="isLeftDrawerOpen"
        class="tw-flex tw-items-center tw-justify-between tw-gap-1 tw-my-2"
      >
        <SfDropdown
          dropdown-id="workflow-list-dropdown"
          :is-open="isDropdownOpen"
          :is-full-width="true"
          :select-class-names="[
            'tw-border-none',
            'tw-shadow-none',
            'hover:tw-border-none',
            'hover:tw-shadow-none',
            'active:tw-shadow-none',
            'active:!tw-bg-transparent',
            'active:!tw-border-none',
          ]"
          :menu-class-names="['tw-shadow-md']"
          unstyled-trigger
          @dropdown-trigger-clicked="toggleDropdown"
          @dropdown-closed="closeDropdown"
        >
          <template #trigger>
            <p
              class="
                tw-text-xs
                tw-text-sf-slate
                tw-mb-0
                tw-mt-0
                tw-pl-1
                tw-flex
                tw-items-center
                tw-gap-1
                tw-cursor-pointer
              "
              data-testid="workflow-list-toggle"
            >
              {{ I18n.t('left_drawer_workflows.all_workflows') }}
              <span
                class="
                  fa fa-chevron-down
                  tw-text-[10px] tw-transition-transform
                "
                :class="{ 'tw-rotate-180': isDropdownOpen }"
              />
            </p>
          </template>
          <template #menu>
            <SfDropdownItem
              v-for="workflow in workflows"
              :key="workflow.id"
              :is-selected="isWorkflowActive(workflow.id)"
              is-short
              :has-border="false"
              is-rounded
              class="!tw-py-1 !tw-px-2 tw-mx-1 tw-my-1 tw-group"
              data-testid="workflow-dropdown-item"
              :href="workflowPath(workflow.id)"
              @click="closeDropdown"
            >
              <template #icon>
                <SfTextAvatar
                  :text="workflow.name"
                  :abbreviation="workflow.abbreviation"
                  :active="isWorkflowActive(workflow.id)"
                  class="tw-mr-2 tw-self-center"
                />
              </template>
              <span
                :class="{
                  'tw-font-bold': isWorkflowActive(workflow.id),
                  'tw-self-center': true,
                }"
              >
                {{ workflow.name }}
              </span>
            </SfDropdownItem>
          </template>
        </SfDropdown>
        <SfButtonDs
          :variant="isSearchActive ? 'primary' : 'secondary'"
          size="sm"
          icon-only
          icon-before="search"
          :aria-label="I18n.t('reconciliation_review.search.label')"
          class="tw-shrink-0"
          data-testid="workflow-search-button"
          @click="toggleSearch"
        />
      </div>
      <div v-show="isLeftDrawerOpen && isSearchActive" class="tw-mb-2 tw-px-1">
        <OverviewNavigationSearch :can-be-focused="true" />
      </div>
      <SfList no-margin :class="{ 'tw-pt-1': !isLeftDrawerOpen }">
        <template v-if="isLeftDrawerOpen">
          <SfListItem
            v-if="selectedWorkflow && !isSearching"
            :item-id="String(selectedWorkflow.id)"
            :selected="isOnWorkflowPage"
            variant="md"
            cornered
            :extra-classes="['!tw-py-1']"
            :content-extra-classes="['tw-min-w-0']"
            :href="workflowPath(selectedWorkflow.id)"
            @item-click="navigateTo(selectedWorkflow.id)"
          >
            <template #icon>
              <SfTextAvatar
                :text="selectedWorkflow.name"
                :abbreviation="selectedWorkflow.abbreviation"
                :active="isOnWorkflowPage"
              />
            </template>
            <SfTruncatedLabel
              :text="selectedWorkflow.name"
              :class="{ 'tw-font-bold': isOnWorkflowPage }"
            />
          </SfListItem>
        </template>
        <template v-else>
          <SfListItem
            v-for="workflow in workflows"
            :key="workflow.id"
            :item-id="String(workflow.id)"
            :selected="isWorkflowActive(workflow.id)"
            variant="md"
            cornered
            is-collapsible
            :is-expanded="false"
            :extra-classes="listItemExtraClasses"
            :icon-extra-classes="listItemIconClasses"
            :content-extra-classes="listItemContentClasses"
            :href="workflowPath(workflow.id)"
            @item-click="navigateTo(workflow.id)"
          >
            <template #icon>
              <SfTextAvatar
                v-tooltip.placementRight="
                  !isLeftDrawerOpen && !disabledMessage ? workflow.name : ''
                "
                :text="workflow.name"
                :abbreviation="workflow.abbreviation"
                :active="isWorkflowActive(workflow.id)"
              />
            </template>
            {{ workflow.name }}
          </SfListItem>
        </template>
      </SfList>
    </template>
    <template v-else>
      <p
        v-show="isLeftDrawerOpen"
        class="tw-text-xs tw-text-sf-slate tw-mb-2 tw-mt-2 tw-pl-1"
      >
        {{ I18n.t('left_drawer_workflows.title') }}
      </p>
      <SfList no-margin :class="{ 'tw-pt-1': !isLeftDrawerOpen }">
        <SfListItem
          v-for="workflow in workflows"
          :key="workflow.id"
          :item-id="String(workflow.id)"
          variant="md"
          cornered
          is-collapsible
          :is-expanded="isLeftDrawerOpen"
          :extra-classes="listItemExtraClasses"
          :icon-extra-classes="listItemIconClasses"
          :content-extra-classes="listItemContentClasses"
          :href="workflowPath(workflow.id)"
          @item-click="navigateTo(workflow.id)"
        >
          <template #icon>
            <SfTextAvatar
              v-tooltip.placementRight="
                !isLeftDrawerOpen && !disabledMessage ? workflow.name : ''
              "
              :text="workflow.name"
              :abbreviation="workflow.abbreviation"
            />
          </template>
          <SfTruncatedLabel :text="workflow.name" />
          <template v-if="isLeftDrawerOpen" #right-content>
            <i
              class="
                fa fa-chevron-right
                tw-invisible
                group-hover:tw-visible
                tw-text-sf-neutral-light-2 tw-text-xs
              "
            />
          </template>
        </SfListItem>
      </SfList>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'

import { SfButtonDs, SfListItem } from '@silverfin/ui-components'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import SfTextAvatar from '@app/shared/vue/components/sf-text-avatar/sf-text-avatar.vue'
import SfTruncatedLabel from '@app/shared/vue/components/sf-truncated-label.vue'
import OverviewNavigationSearch from '@app/application/workflow-overview-navigation/components/overview-navigation-search.vue'

import { storeToRefs } from 'pinia'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useWorkflowOverviewSearch } from '@app/application/workflow-overview-navigation/store/search'

import { getCompanyWorkflows } from '../api'
import type { LeftDrawerWorkflow } from '../types'

defineOptions({ name: 'WorkflowList' })

const drawerStore = useDrawer()
const searchStore = useWorkflowOverviewSearch()

interface Props {
  workflowId?: number | null
  isOnWorkflowPage?: boolean
  disabledMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  workflowId: null,
  isOnWorkflowPage: false,
  disabledMessage: undefined,
})

const I18n = useI18n()
const workflows = ref<LeftDrawerWorkflow[]>([])
const isDropdownOpen = ref(false)

const { isLeftDrawerOpen } = storeToRefs(drawerStore)
const { searchTerm, isActive: isSearchActive } = storeToRefs(searchStore)

const hasSelectedWorkflow = computed(() => props.workflowId !== null)
const isSearching = computed(() => searchTerm.value !== '')

function toggleSearch(): void {
  if (props.disabledMessage) return

  searchStore.toggleSearch()
}

const isWorkflowActive = (workflowId: number): boolean =>
  props.isOnWorkflowPage && workflowId === props.workflowId

const selectedWorkflow = computed(() =>
  workflows.value.find((w) => w.id === props.workflowId)
)

function toggleDropdown(): void {
  if (props.disabledMessage) return
  isDropdownOpen.value = !isDropdownOpen.value
}

function closeDropdown(): void {
  isDropdownOpen.value = false
}

// Exposed as an href on every row so cmd/middle/right-click open the workflow in
// a new tab. Undefined while navigation is unavailable, which makes SfListItem
// fall back to its inert `href="#"` placeholder.
function workflowPath(workflowId: number): string | undefined {
  if (props.disabledMessage) return undefined

  const ledgerId = window.Silverfin.currentLedgerId()
  if (!ledgerId) return undefined

  return window.Silverfin.Routes.ledger_company_workflow_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    ledgerId,
    workflowId
  )
}

function navigateTo(workflowId: number): void {
  const path = workflowPath(workflowId)
  if (!path) return

  window.Turbolinks.visit(path)
}

const listItemExtraClasses = computed(() => {
  if (!isLeftDrawerOpen.value) {
    return ['!tw-py-1', '!tw-px-1', '!tw-bg-transparent', '[&+&]:!tw-mt-1']
  }

  return ['!tw-py-1']
})

const listItemIconClasses = computed(() => {
  if (!isLeftDrawerOpen.value) {
    return ['!tw-mr-0', 'tw-flex-1']
  }

  return []
})

const listItemContentClasses = computed(() => {
  if (!isLeftDrawerOpen.value) {
    return ['tw-hidden']
  }

  return ['tw-min-w-0']
})

if (hasSelectedWorkflow.value) {
  searchStore.registerHeaderSearch()
}

onBeforeUnmount(() => {
  searchStore.unregisterHeaderSearch()
  searchStore.closeSearch()
})

onMounted(async () => {
  const result = await getCompanyWorkflows()
  if (result) {
    workflows.value = result
  }
})
</script>
